import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, ShoppingCart, DollarSign } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api, ApiError, type LooseCondition, type BatchOrderRow } from "@/lib/api";

const LOOSE_CONDITIONS: { value: LooseCondition; label: string }[] = [
  { value: "MT", label: "Mint" },
  { value: "NM", label: "Near Mint" },
  { value: "EX", label: "Excellent" },
  { value: "GD", label: "Good" },
  { value: "LP", label: "Light Played" },
  { value: "PL", label: "Played" },
  { value: "PO", label: "Poor" },
];

const GRADED_GRADERS = ["PSA", "Collectaura"] as const;
const GRADE_SCALE = ["10", "9.5", "9", "8.5", "8", "7", "6", "5", "4", "3", "2", "1"];

type LooseRowState = { quantity: string; price: string };
type GradedRow = { grader: string; grade: string; price: string; quantity: string };

/**
 * Grille BUY/SELL par état (loose + gradée), remplace l'ancien dialogue à
 * état unique. Le prix dépend fondamentalement de l'état de la carte — un
 * BUY/SELL plat n'a pas de sens.
 *
 * - Loose (MT..PO) : quantité + prix par ligne, toujours visible (grille
 *   fixe, faible cardinalité). Soumis en direct (batch) dans les deux modes.
 * - Gradée (PSA/Collectaura) : pas de grille fixe ~20 lignes (bruit visuel
 *   pour une action rare) — un ajouteur de ligne (select grader + grade).
 *   Mode buy : quantité+prix, accumulé puis soumis avec le loose en un seul
 *   /api/wtb/batch. Mode sell : prix seul, "Publier" navigue vers /sell
 *   (vérification de certificat obligatoire, jamais contournée) — pas de
 *   notion de quantité, un cert_number est unique par carte physique.
 */
export function TradeGridDialog({
  open,
  onClose,
  mode,
  cardId,
  cardName,
  wallet,
  onConnect,
  onPosted,
}: {
  open: boolean;
  onClose: () => void;
  mode: "buy" | "sell";
  cardId: string;
  cardName: string;
  wallet: string | null;
  onConnect: () => Promise<void>;
  onPosted: () => void;
}) {
  const navigate = useNavigate();
  const isBuy = mode === "buy";

  const [looseRows, setLooseRows] = useState<Record<LooseCondition, LooseRowState>>(() =>
    Object.fromEntries(LOOSE_CONDITIONS.map((c) => [c.value, { quantity: "", price: "" }])) as Record<
      LooseCondition,
      LooseRowState
    >,
  );
  const [gradedRows, setGradedRows] = useState<GradedRow[]>([]);
  const [pickGrader, setPickGrader] = useState<string>(GRADED_GRADERS[0]);
  const [pickGrade, setPickGrade] = useState<string>(GRADE_SCALE[0]);
  const [pickPrice, setPickPrice] = useState("");
  const [pickQuantity, setPickQuantity] = useState("1");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const resetAndClose = () => {
    setLooseRows(
      Object.fromEntries(LOOSE_CONDITIONS.map((c) => [c.value, { quantity: "", price: "" }])) as Record<
        LooseCondition,
        LooseRowState
      >,
    );
    setGradedRows([]);
    setPickPrice("");
    setPickQuantity("1");
    setErr(null);
    onClose();
  };

  const updateLoose = (c: LooseCondition, field: keyof LooseRowState, value: string) => {
    setLooseRows((prev) => ({ ...prev, [c]: { ...prev[c], [field]: value } }));
  };

  const addGradedRow = () => {
    const price = Number(pickPrice);
    const quantity = Number(pickQuantity) || 1;
    if (!Number.isFinite(price) || price <= 0) {
      setErr("Prix invalide.");
      return;
    }
    setGradedRows((prev) => [
      ...prev,
      { grader: pickGrader, grade: pickGrade, price: String(price), quantity: String(quantity) },
    ]);
    setPickPrice("");
    setPickQuantity("1");
    setErr(null);
  };

  const removeGradedRow = (idx: number) => {
    setGradedRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const publishGraded = (row: GradedRow) => {
    const params = new URLSearchParams({
      card_id: cardId,
      grader: row.grader,
      grade: row.grade,
      price: row.price,
    });
    resetAndClose();
    navigate(`/sell?${params.toString()}`);
  };

  const submitBatch = async () => {
    if (!wallet) {
      try {
        await onConnect();
      } catch (e) {
        setErr((e as Error).message);
      }
      return;
    }
    setErr(null);

    const rows: BatchOrderRow[] = [];
    for (const c of LOOSE_CONDITIONS) {
      const r = looseRows[c.value];
      const qty = Number(r.quantity);
      const price = Number(r.price);
      if (!qty && !price) continue;
      if (!Number.isInteger(qty) || qty < 1) {
        setErr(`Quantité invalide pour ${c.label}.`);
        return;
      }
      if (!Number.isFinite(price) || price <= 0) {
        setErr(`Prix invalide pour ${c.label}.`);
        return;
      }
      rows.push({ grader: "RAW", condition: c.value, price_usdc: price, quantity: qty });
    }
    if (isBuy) {
      for (const g of gradedRows) {
        rows.push({
          grader: g.grader as "PSA" | "Collectaura",
          grade: g.grade,
          price_usdc: Number(g.price),
          quantity: Number(g.quantity) || 1,
        });
      }
    }

    if (rows.length === 0) {
      setErr("Remplis au moins une ligne (quantité + prix).");
      return;
    }

    setBusy(true);
    try {
      const endpoint = isBuy ? "/api/wtb/batch" : "/api/listings/batch";
      await api(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ card_id: cardId, rows }),
      });
      onPosted();
      resetAndClose();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      className="max-w-2xl"
      title={
        <span className="inline-flex items-center gap-1.5">
          {isBuy ? <ShoppingCart className="h-4 w-4 text-success" /> : <DollarSign className="h-4 w-4 text-danger" />}
          {isBuy ? "Placer des offres d'achat" : "Mettre en vente"}
        </span>
      }
      description={
        <>
          <span className="font-mono">{cardId}</span> · {cardName}
        </>
      }
      footer={
        <>
          <Button variant="outline" size="sm" onClick={resetAndClose}>Annuler</Button>
          <Button size="sm" onClick={submitBatch} loading={busy} disabled={!wallet && busy}>
            {wallet ? (isBuy ? "Publier les offres" : "Publier les annonces loose") : "Connecter le wallet"}
          </Button>
        </>
      }
    >
      {!wallet ? (
        <p className="mb-3 text-xs text-warn">Connecte ton wallet pour placer un ordre.</p>
      ) : null}

      <div className="space-y-5">
        {/* Loose : grille fixe, quantité + prix par état */}
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-fg">
            Loose (non gradée)
          </div>
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-xs">
              <thead className="bg-surface-2/40 text-[10px] uppercase tracking-wide text-muted-fg">
                <tr>
                  <th className="px-3 py-1.5 text-left">État</th>
                  <th className="px-3 py-1.5 text-right">Quantité</th>
                  <th className="px-3 py-1.5 text-right">Prix (USDC)</th>
                </tr>
              </thead>
              <tbody className="[&_tr]:border-t [&_tr]:border-border/60">
                {LOOSE_CONDITIONS.map((c) => (
                  <tr key={c.value}>
                    <td className="px-3 py-1.5">
                      <Badge tone="neutral" variant="soft" className="font-mono">{c.value}</Badge>
                      <span className="ml-1.5 text-muted-fg">{c.label}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={looseRows[c.value].quantity}
                        onChange={(e) => updateLoose(c.value, "quantity", e.target.value)}
                        placeholder="0"
                        className="h-7 w-16 rounded border border-border bg-surface px-1.5 text-right text-xs"
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={looseRows[c.value].price}
                        onChange={(e) => updateLoose(c.value, "price", e.target.value)}
                        placeholder="0.00"
                        className="h-7 w-20 rounded border border-border bg-surface px-1.5 text-right text-xs"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Gradée : ajouteur de ligne (pas de grille fixe ~20 lignes) */}
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-fg">
            Gradée (PSA / Collectaura)
          </div>
          <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-surface p-2.5">
            <label className="flex flex-col gap-1 text-[10px] text-muted-fg">
              Grader
              <select
                value={pickGrader}
                onChange={(e) => setPickGrader(e.target.value)}
                className="h-8 rounded-md border border-border bg-surface px-2 text-xs"
              >
                {GRADED_GRADERS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[10px] text-muted-fg">
              Note
              <select
                value={pickGrade}
                onChange={(e) => setPickGrade(e.target.value)}
                className="h-8 rounded-md border border-border bg-surface px-2 text-xs"
              >
                {GRADE_SCALE.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </label>
            {isBuy ? (
              <label className="flex flex-col gap-1 text-[10px] text-muted-fg">
                Quantité
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={pickQuantity}
                  onChange={(e) => setPickQuantity(e.target.value)}
                  className="h-8 w-16 rounded-md border border-border bg-surface px-2 text-xs"
                />
              </label>
            ) : null}
            <label className="flex flex-col gap-1 text-[10px] text-muted-fg">
              Prix (USDC)
              <Input
                type="number"
                min="0"
                step="0.01"
                value={pickPrice}
                onChange={(e) => setPickPrice(e.target.value)}
                placeholder="0.00"
                className="h-8 w-24"
              />
            </label>
            {isBuy ? (
              <Button size="sm" variant="secondary" onClick={addGradedRow}>
                <Plus className="h-3.5 w-3.5" /> Ajouter
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => publishGraded({ grader: pickGrader, grade: pickGrade, price: pickPrice, quantity: "1" })}
                disabled={!Number(pickPrice)}
              >
                Publier
              </Button>
            )}
          </div>

          {isBuy && gradedRows.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {gradedRows.map((g, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs"
                >
                  <span>
                    <Badge tone="info" variant="soft">{g.grader} {g.grade}</Badge>
                    <span className="ml-2 text-muted-fg">×{g.quantity}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono">{g.price} USDC</span>
                    <button
                      type="button"
                      onClick={() => removeGradedRow(i)}
                      className="text-muted-fg hover:text-danger"
                      aria-label="Retirer"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {!isBuy ? (
          <p className="text-[11px] text-muted-fg">
            Une carte gradée exige un certificat vérifié : cliquer "Publier"
            t'emmène sur la page de vente dédiée, préremplie.
          </p>
        ) : null}

        {err ? <p className="text-xs text-danger">{err}</p> : null}
      </div>
    </Dialog>
  );
}
